import type { PublicPlayerConfig } from "./types";
import { mergeSettings } from "./types";
import { VslPlayer } from "./player";
import { lsGet, lsSet } from "./storage";

// Origin that served p.js — config and hls.js come from the same place (server or CDN).
const script = document.currentScript as HTMLScriptElement | null;
const ORIGIN = (() => {
  try {
    return new URL(script?.src || location.href).origin;
  } catch {
    return location.origin;
  }
})();

/** "blocked" = this page's domain is not on the VSL's allow-list. */
type ConfigResult = PublicPlayerConfig | "blocked" | null;

async function fetchConfig(id: string): Promise<ConfigResult> {
  const cacheKey = "vsl:cfg:" + id;
  try {
    // "no-cache": the browser may keep its copy but must ask the CDN first. Its
    // stale-while-revalidate would otherwise show a returning viewer yesterday's
    // settings (no new pixel, old texts) for one more visit.
    const res = await fetch(`${ORIGIN}/api/p/${encodeURIComponent(id)}`, { credentials: "omit", cache: "no-cache" });
    // A domain block must not be papered over with the cached copy.
    if (res.status === 403) return "blocked";
    if (!res.ok) throw new Error(String(res.status));
    const cfg = (await res.json()) as PublicPlayerConfig;
    lsSet(cacheKey, JSON.stringify(cfg));
    return cfg;
  } catch {
    // Server unreachable: fall back to the last config this browser saw.
    const cached = lsGet(cacheKey);
    return cached ? (JSON.parse(cached) as PublicPlayerConfig) : null;
  }
}

/**
 * Campaign config: the server applies the campaign's rules (country, UTM tags,
 * language, platform, OS) and answers with the chosen VSL's own config, so
 * everything downstream — stats, pixels, signed links — is unchanged.
 */
async function fetchCampaignConfig(id: string): Promise<ConfigResult> {
  // The page address carries the UTM tags; the country and device are read
  // from the request itself on the server.
  const q = new URLSearchParams();
  try {
    q.set("u", location.href.slice(0, 500));
    q.set("l", (navigator.language || "").slice(0, 16));
    q.set("tp", String(navigator.maxTouchPoints || 0));
  } catch {
    /* nothing to report about this page */
  }
  const cacheKey = "vsl:camp:" + id + ":" + (location.search || "");
  try {
    const res = await fetch(`${ORIGIN}/api/c/${encodeURIComponent(id)}?${q}`, { credentials: "omit" });
    if (res.status === 403) return "blocked";
    if (!res.ok) throw new Error(String(res.status));
    const cfg = (await res.json()) as PublicPlayerConfig;
    lsSet(cacheKey, JSON.stringify(cfg));
    return cfg;
  } catch {
    // Server unreachable: the video this browser saw last is better than a black box.
    const cached = lsGet(cacheKey);
    return cached ? (JSON.parse(cached) as PublicPlayerConfig) : null;
  }
}

class VslPlayerElement extends HTMLElement {
  player: VslPlayer | null = null;
  private _config: PublicPlayerConfig | null = null;
  private mounted = false;

  /** Dashboard preview: set a config object directly (no network, no tracking). */
  set config(cfg: PublicPlayerConfig | null) {
    this._config = cfg;
    if (this.isConnected) this.mount();
  }
  get config() {
    return this._config;
  }

  connectedCallback() {
    if (!this.mounted) this.mount();
  }

  disconnectedCallback() {
    this.player?.destroy();
    this.player = null;
    this.mounted = false;
  }

  /** vturb-compatible API on the element itself; calls made before the player is ready are queued. */
  private pending: [number | string, string[] | string, { persist?: boolean; callback?: () => void } | undefined][] = [];
  displayHiddenElements(
    seconds: number | string,
    selectors: string[] | string,
    opts?: { persist?: boolean; callback?: () => void },
  ) {
    if (this.player) this.player.displayHiddenElements(seconds, selectors, opts);
    else this.pending.push([seconds, selectors, opts]);
  }

  private playerId(): string {
    return (this.getAttribute("data-player") || this.id || "").replace(/^vsl-/, "");
  }

  /** Domain not allowed: say why, so a mis-set embed isn't mistaken for a broken player. */
  private showBlocked() {
    const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
    root.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;width:100%;aspect-ratio:16/9;' +
      'background:#0d0926;color:#8e8aa8;font:500 14px/1.5 system-ui,sans-serif;text-align:center;padding:16px;' +
      'box-sizing:border-box">This video is not allowed to play on this domain</div>';
  }

  private async mount() {
    this.mounted = true;
    const preview = this.hasAttribute("preview");
    const campaign = this.getAttribute("data-campaign")?.trim();
    const cfg =
      this._config ??
      (preview ? null : campaign ? await fetchCampaignConfig(campaign) : await fetchConfig(this.playerId()));
    if (cfg === "blocked") return this.showBlocked();
    if (!cfg || !cfg.video) return; // keep the placeholder
    cfg.settings = mergeSettings(cfg.settings);
    const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
    this.player?.destroy();
    this.player = new VslPlayer(this, root, cfg, ORIGIN + "/", preview);
    for (const args of this.pending.splice(0)) this.player.displayHiddenElements(...args);
  }
}

if (!customElements.get("vsl-player")) {
  customElements.define("vsl-player", VslPlayerElement);
}
// Drop-in replacement for vturb embeds: existing landing code that looks up
// <vturb-smartplayer> (e.g. displayHiddenElements for the CTA) keeps working.
if (!customElements.get("vturb-smartplayer")) {
  customElements.define("vturb-smartplayer", class extends VslPlayerElement {});
}

/**
 * vturb landings use this helper (it came from vturb's script) to reach the <video>
 * inside the player's shadow DOM. Keep it so their tracking code works unchanged.
 */
function findElementInLitElement(root: Element | null, predicate: (el: Element) => boolean): Element | null {
  const queue: (Element | ShadowRoot)[] = root ? [root] : [];
  while (queue.length) {
    const node = queue.shift()!;
    const children = node instanceof ShadowRoot ? [...node.children] : [...node.children];
    if (node instanceof Element && (node as Element & { shadowRoot?: ShadowRoot }).shadowRoot) {
      queue.push((node as Element & { shadowRoot: ShadowRoot }).shadowRoot);
    }
    for (const child of children) {
      if (predicate(child)) return child;
      queue.push(child);
    }
  }
  return null;
}
(window as unknown as { findElementInLitElement?: typeof findElementInLitElement }).findElementInLitElement ??=
  findElementInLitElement;

// Small global for debugging on landing pages.
(window as unknown as { VSLGO: object }).VSLGO = { version: "0.1.0", origin: ORIGIN };
